const PALETTE_MIME = "application/x-wa-flow-type";
const ITEM_MIME = "application/x-wa-flow-item";

/** Meta recommended Flow JSON version. 5.0 and earlier frozen versions cannot be published. */
export const RECOMMENDED_FLOW_JSON_VERSION = "7.3";
const FROZEN_FLOW_JSON_VERSIONS = new Set(["2.1", "3.0", "3.1", "4.0", "5.0"]);

export function isFrozenFlowJsonVersion(version) {
  return FROZEN_FLOW_JSON_VERSIONS.has(String(version || "").trim());
}

function ensureRoutingModel(parsed) {
  if (parsed.routing_model && typeof parsed.routing_model === "object") {
    return parsed;
  }
  const screens = Array.isArray(parsed.screens) ? parsed.screens : [];
  const routing_model = {};
  screens.forEach((screen, index) => {
    const id = screen?.id;
    if (!id) return;
    routing_model[id] = screens[index + 1]?.id ? [screens[index + 1].id] : [];
  });
  parsed.routing_model = routing_model;
  return parsed;
}

export function migrateFrozenFlowJson(json) {
  const parsed =
    json == null
      ? {}
      : typeof json === "string"
        ? JSON.parse(json)
        : { ...json };
  const version = String(parsed.version || "").trim();
  if (!version || isFrozenFlowJsonVersion(version)) {
    parsed.version = RECOMMENDED_FLOW_JSON_VERSION;
  }
  return ensureRoutingModel(parsed);
}

function uid(prefix = "c") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function fieldName(type) {
  return `${String(type || "field")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase()}_${uid("n").slice(-4)}`;
}

export const FLOW_COMPONENT_GROUPS = [
  {
    id: "text",
    label: "Text",
    items: [
      { type: "TextHeading", label: "Heading" },
      { type: "TextSubheading", label: "Subheading" },
      { type: "TextBody", label: "Body" },
      { type: "TextCaption", label: "Caption" },
    ],
  },
  {
    id: "input",
    label: "Inputs",
    items: [
      { type: "TextInput", label: "Text input" },
      { type: "TextArea", label: "Text area" },
      { type: "DatePicker", label: "Date picker" },
      { type: "Dropdown", label: "Dropdown" },
      { type: "RadioButtonsGroup", label: "Radio group" },
      { type: "CheckboxGroup", label: "Checkbox group" },
      { type: "OptIn", label: "Opt-in" },
    ],
  },
  {
    id: "media",
    label: "Media",
    items: [
      { type: "Image", label: "Image" },
      { type: "EmbeddedLink", label: "Link" },
    ],
  },
  {
    id: "action",
    label: "Action",
    items: [{ type: "Footer", label: "Footer button" }],
  },
];

const DEFAULT_OPTIONS = [
  { id: "option_1", title: "Option 1" },
  { id: "option_2", title: "Option 2" },
];

export function createFlowComponent(type) {
  const _id = uid();
  switch (type) {
    case "TextHeading":
      return { _id, type, text: "Heading" };
    case "TextSubheading":
      return { _id, type, text: "Subheading" };
    case "TextBody":
      return { _id, type, text: "Add supporting text" };
    case "TextCaption":
      return { _id, type, text: "Caption" };
    case "TextInput":
      return {
        _id,
        type,
        name: fieldName("text"),
        label: "Text",
        required: true,
        "input-type": "text",
        "helper-text": "",
      };
    case "TextArea":
      return {
        _id,
        type,
        name: fieldName("notes"),
        label: "Notes",
        required: false,
        "helper-text": "",
      };
    case "DatePicker":
      return {
        _id,
        type,
        name: fieldName("date"),
        label: "Date",
        required: true,
      };
    case "Dropdown":
      return {
        _id,
        type,
        name: fieldName("choice"),
        label: "Select",
        required: true,
        "data-source": DEFAULT_OPTIONS.map((item) => ({ ...item })),
      };
    case "RadioButtonsGroup":
      return {
        _id,
        type,
        name: fieldName("radio"),
        label: "Choose one",
        required: true,
        "data-source": DEFAULT_OPTIONS.map((item) => ({ ...item })),
      };
    case "CheckboxGroup":
      return {
        _id,
        type,
        name: fieldName("checks"),
        label: "Choose any",
        required: false,
        "data-source": DEFAULT_OPTIONS.map((item) => ({ ...item })),
      };
    case "OptIn":
      return {
        _id,
        type,
        name: fieldName("optin"),
        label: "I agree to be contacted",
        required: true,
      };
    case "Image":
      return {
        _id,
        type,
        src: "",
        height: 200,
        "scale-type": "contain",
        "alt-text": "Image",
      };
    case "EmbeddedLink":
      return {
        _id,
        type,
        text: "Learn more",
        "on-click-action": {
          name: "open_url",
          url: "https://example.com",
        },
      };
    case "Footer":
      return {
        _id,
        type,
        label: "Continue",
        "on-click-action": { name: "complete", payload: {} },
      };
    default:
      return { _id, type, text: type };
  }
}

export function createFlowScreen(index = 0) {
  const n = Number(index) + 1;
  return {
    id: index === 0 ? "WELCOME_SCREEN" : `SCREEN_${n}`,
    title: index === 0 ? "Welcome" : `Screen ${n}`,
    terminal: true,
    components: [
      createFlowComponent("TextHeading"),
      createFlowComponent("Footer"),
    ],
  };
}

function flattenLayoutChildren(nodes = []) {
  const out = [];
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    if (node.type === "Form" || node.type === "SingleColumnLayout") {
      out.push(...flattenLayoutChildren(node.children || []));
      continue;
    }
    out.push({ ...node, _id: node._id || uid() });
  }
  return out;
}

export function parseFlowJsonToScreens(jsonText) {
  try {
    const parsed = JSON.parse(jsonText || "{}");
    const screens = Array.isArray(parsed.screens) ? parsed.screens : [];
    if (!screens.length) return [createFlowScreen(0)];
    return screens.map((screen, index) => ({
      id: screen.id || `SCREEN_${index + 1}`,
      title: screen.title || `Screen ${index + 1}`,
      terminal: Boolean(screen.terminal),
      components: flattenLayoutChildren(screen.layout?.children || []),
    }));
  } catch {
    return [createFlowScreen(0)];
  }
}

function stripEditorMeta(component) {
  const next = { ...component };
  delete next._id;
  Object.keys(next).forEach((key) => {
    if (next[key] === "" || next[key] == null) delete next[key];
  });
  return next;
}

function footerForScreen(screen, nextScreen) {
  const existing = (screen.components || []).find((item) => item.type === "Footer");
  const footer = existing ? { ...existing } : createFlowComponent("Footer");
  if (nextScreen) {
    footer.label = footer.label || "Continue";
    footer["on-click-action"] = {
      name: "navigate",
      next: { type: "screen", name: nextScreen.id },
      payload: {},
    };
  } else {
    footer.label = footer.label === "Continue" ? "Complete" : footer.label || "Complete";
    footer["on-click-action"] = { name: "complete", payload: {} };
  }
  return footer;
}

export function screensToFlowJson(screens) {
  const list = (screens || []).length ? screens : [createFlowScreen(0)];
  const routing_model = {};
  const jsonScreens = list.map((screen, index) => {
    const nextScreen = list[index + 1];
    const isLast = !nextScreen;
    routing_model[screen.id] = nextScreen ? [nextScreen.id] : [];
    const body = (screen.components || []).filter((item) => item.type !== "Footer");
    const footer = footerForScreen(screen, nextScreen);
    return {
      id: String(screen.id || `SCREEN_${index + 1}`)
        .replace(/\s+/g, "_")
        .replace(/[^A-Z0-9_]/gi, "")
        .toUpperCase() || `SCREEN_${index + 1}`,
      title: screen.title || `Screen ${index + 1}`,
      terminal: isLast,
      success: isLast,
      data: {},
      layout: {
        type: "SingleColumnLayout",
        children: [
          {
            type: "Form",
            name: "flow_form",
            children: [...body, footer].map(stripEditorMeta),
          },
        ],
      },
    };
  });

  return {
    version: RECOMMENDED_FLOW_JSON_VERSION,
    routing_model,
    screens: jsonScreens,
  };
}

export function stringifyFlowScreens(screens) {
  return JSON.stringify(screensToFlowJson(screens), null, 2);
}

export function optionsToText(options) {
  return (Array.isArray(options) ? options : [])
    .map((item) => item.title || item.id)
    .join("\n");
}

export function textToOptions(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((title, index) => ({
      id: `option_${index + 1}`,
      title,
    }));
}

export { PALETTE_MIME, ITEM_MIME, uid };
